import { DateRange, DateValue, parseISODate, RangeValue } from '@cad-arc';
import React, { useState } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import { useIntegratedForm } from '../use-integrated-form';
import { CommonControlProps, IntegratedFormControl } from './integrated-form-control';

function toFormFriendlyDateRange(
    dateRange: RangeValue<DateValue> | null
): RangeValue<string> | null {
    if (!dateRange) {
        return null;
    }
    return {
        start: dateRange.start
            .toDate(Intl.DateTimeFormat().resolvedOptions().timeZone)
            .toISOString(),
        end: dateRange.end.toDate(Intl.DateTimeFormat().resolvedOptions().timeZone).toISOString(),
    };
}

function toDateRangePickerFriendlyDateRange(
    dateRange: RangeValue<string> | null
): RangeValue<DateValue> | null {
    if (!dateRange) {
        return null;
    }
    return {
        start: parseISODate(dateRange.start, 'absolute'),
        end: parseISODate(dateRange.end, 'absolute'),
    };
}

type DateRangeProps = React.ComponentProps<typeof DateRange>;
type Props<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = CommonControlProps<TFieldValues, TName> & {
    dateRangeProps: DateRangeProps;
};

export function FormDateRange<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(props: Props<TFieldValues, TName>): React.ReactElement {
    const integratedForm = useIntegratedForm();
    const [dateRange, setDateRange] = useState<RangeValue<DateValue> | null>(
        toDateRangePickerFriendlyDateRange(integratedForm.form.getValues()[props.name])
    );
    const { dateRangeProps, ...integratedFormControlProps } = props;
    const renderViewMode = React.useCallback(() => {
        return 'date range serializer not implemented';
    }, []);

    return (
        <IntegratedFormControl {...integratedFormControlProps} renderViewMode={renderViewMode}>
            {(inputProps) => (
                // @ts-expect-error error with ref type, TODO figure this out w/ arc team
                <DateRange
                    {...inputProps}
                    {...dateRangeProps}
                    value={dateRange}
                    onChange={(dateValue) => {
                        setDateRange(dateValue);
                        inputProps.onChange(toFormFriendlyDateRange(dateValue));
                    }}
                />
            )}
        </IntegratedFormControl>
    );
}
